// src/components/FilterModal.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiX, FiCheck } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";

const FilterModal = ({ isOpen, onClose, onApply }) => {
  const [filters, setFilters] = useState({
    distributor_id: "",
    outlet_id: "",
    status: "",
    method: "",
  });

  const [outlets, setOutlets] = useState([]);
  const [distributors, setDistributors] = useState([]);

  // search input states
  const [searchDistributor, setSearchDistributor] = useState("");
  const [searchOutlet, setSearchOutlet] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isOpen) {
      fetchAllDistributors();
      fetchAllOutlets();
    }
  }, [isOpen]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ===========================================================
  // FETCH ALL DISTRIBUTORS (loop all pages)
  // ===========================================================
  const fetchAllDistributors = async () => {
    try {
      const first = await axios.get(
        `${BASE_URL}/v1/distributor?page=1`,
        getAuthHeaders()
      );

      let all = [...first.data.data];
      const totalPages = first.data.meta.last_page;

      for (let p = 2; p <= totalPages; p++) {
        const r = await axios.get(
          `${BASE_URL}/v1/distributor?page=${p}`,
          getAuthHeaders()
        );
        all = [...all, ...r.data.data];
      }

      setDistributors(all);
    } catch (err) {
      console.error("Error fetching distributors:", err);
    }
  };

  // ===========================================================
  // FETCH ALL OUTLETS (loop all pages)
  // ===========================================================
  const fetchAllOutlets = async () => {
    try {
      const first = await axios.get(
        `${BASE_URL}/v1/outlet?page=1`,
        getAuthHeaders()
      );

      let all = [...first.data.data];
      const totalPages = first.data.meta.last_page;

      for (let p = 2; p <= totalPages; p++) {
        const r = await axios.get(
          `${BASE_URL}/v1/outlet?page=${p}`,
          getAuthHeaders()
        );
        all = [...all, ...r.data.data];
      }

      setOutlets(all);
    } catch (err) {
      console.error("Error fetching outlets:", err);
    }
  };

  // ===========================================================
  // HANDLE CHANGE FILTER
  // ===========================================================
  const handleSelectDistributor = (id, name) => {
    setFilters((prev) => ({ ...prev, distributor_id: id }));
    setSearchDistributor(name);
  };

  const handleSelectOutlet = (id, name) => {
    setFilters((prev) => ({ ...prev, outlet_id: id }));
    setSearchOutlet(name);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-xl border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Filter Purchasing Order
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* SEARCHABLE DISTRIBUTOR */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Distributor
            </label>

            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search distributor..."
                value={searchDistributor}
                onChange={(e) => {
                  setSearchDistributor(e.target.value);
                  setFilters((prev) => ({ ...prev, distributor_id: "" }));
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-xl shadow-sm"
              />
            </div>

            {/* Dropdown filtered distributor */}
            {searchDistributor !== "" && (
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-xl shadow bg-white">
                {distributors
                  .filter((d) =>
                    d.name.toLowerCase().includes(searchDistributor.toLowerCase())
                  )
                  .map((d) => (
                    <div
                      key={d.id}
                      className="px-4 py-2 hover:bg-green-100 cursor-pointer"
                      onClick={() => handleSelectDistributor(d.id, d.name)}
                    >
                      {d.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* SEARCHABLE OUTLET */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Outlet
            </label>

            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search outlet..."
                value={searchOutlet}
                onChange={(e) => {
                  setSearchOutlet(e.target.value);
                  setFilters((prev) => ({ ...prev, outlet_id: "" }));
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-xl shadow-sm"
              />
            </div>

            {/* Dropdown filtered outlet */}
            {searchOutlet !== "" && (
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-xl shadow bg-white">
                {outlets
                  .filter((o) =>
                    o.name.toLowerCase().includes(searchOutlet.toLowerCase())
                  )
                  .map((o) => (
                    <div
                      key={o.id}
                      className="px-4 py-2 hover:bg-green-100 cursor-pointer"
                      onClick={() => handleSelectOutlet(o.id, o.name)}
                    >
                      {o.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* STATUS */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              className="w-full px-4 py-2 border rounded-xl shadow-sm"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="received">Received</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>

          {/* METHOD */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Method
            </label>
            <select
              name="method"
              value={filters.method}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, method: e.target.value }))
              }
              className="w-full px-4 py-2 border rounded-xl shadow-sm"
            >
              <option value="">All</option>
              <option value="cash">Cash</option>
              <option value="credit">Credit</option>
            </select>
          </div>

          {/* BUTTONS */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl flex items-center gap-2"
            >
              <FiX className="text-lg" />
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-xl flex items-center gap-2"
            >
              <FiCheck className="text-lg" />
              Apply
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default FilterModal;
